import React, {useEffect, useState} from "react";
import {Helmet} from "react-helmet-async";
import {
    BackLink,
    ContactLine,
    CopyField,
    DownloadCard,
    Steps,
    SupportHeading,
    SupportLayout,
    useDownloadFlow,
} from "../components/support/SupportUI";
import {
    detectMacVariant,
    fallbackDownloadUrl,
    MacVariant,
    SUPPORT_PHONE,
    useRustdeskInfo,
} from "../components/support/rustdesk";

const variantLabel: Record<MacVariant, string> = {
    "mac-arm": "Für Mac mit Apple-Chip",
    "mac-intel": "Für Mac mit Intel-Prozessor",
};

const altLinkText: Record<MacVariant, string> = {
    // Text für den Link zur jeweils *anderen* Variante
    "mac-intel": "Älterer Mac mit Intel-Prozessor?",
    "mac-arm": "Neuerer Mac mit Apple-Chip (M1 oder neuer)?",
};

const SupportMac = () => {
    const api = useRustdeskInfo();
    const [variant, setVariant] = useState<MacVariant | null>(null);
    const flow = useDownloadFlow(api, variant);

    useEffect(() => {
        let cancelled = false;
        detectMacVariant().then(v => {
            if (!cancelled) setVariant(current => current ?? v);
        });
        return () => {
            cancelled = true;
        };
    }, []);

    const alt: MacVariant | null = variant && (variant === "mac-arm" ? "mac-intel" : "mac-arm");
    const altDownload = alt && api.status === "ok" ? api.info.downloads[alt] : undefined;
    const altUrl = alt ? altDownload?.url ?? fallbackDownloadUrl(alt) : undefined;
    const showAlt = alt && altUrl && api.status !== "loading" && (api.status === "error" || altDownload?.available);

    const idServer = api.status === "ok" ? api.info.idServer : undefined;
    const publicKey = api.status === "ok" ? api.info.publicKey : undefined;

    return (
        <SupportLayout>
            <Helmet>
                <title>Fernwartung für Mac | Dockline Digital</title>
                <meta name="robots" content="noindex, nofollow"/>
            </Helmet>

            <SupportHeading eyebrow="Fernwartung · Mac" title="RustDesk für Mac"/>

            <DownloadCard {...flow} variantLabel={variant ? variantLabel[variant] : undefined}/>

            {showAlt && (
                <p className="sp-alt sp-enter sp-enter-1">
                    {altLinkText[alt]}{" "}
                    <a
                        href={altUrl}
                        onClick={() => {
                            setVariant(alt);
                            flow.markStarted();
                        }}
                    >
                        Diese Version laden
                    </a>
                </p>
            )}

            <Steps>
                <li>
                    <strong>Datei öffnen</strong> und RustDesk in den Ordner „Programme“ ziehen. Falls macOS
                    nachfragt, wählen Sie „Öffnen“.
                </li>
                <li>
                    <strong>Zugriff erlauben.</strong> Beim ersten Start fragt macOS nach „Bildschirmaufnahme“
                    und „Bedienungshilfen“. Erlauben Sie beides unter Systemeinstellungen → Datenschutz &amp;
                    Sicherheit.
                </li>
                <li>
                    <strong>Unseren Server eintragen.</strong> Öffnen Sie in RustDesk Einstellungen → Netzwerk →
                    „ID-/Relay-Server“ und tragen Sie diese beiden Werte ein. Die übrigen Felder bleiben leer.
                    {idServer && publicKey ? (
                        <div className="sp-fields">
                            <CopyField label="ID-Server" value={idServer}/>
                            <CopyField label="Key" value={publicKey}/>
                        </div>
                    ) : api.status === "loading" ? (
                        <div className="sp-fields sp-fields-loading" aria-hidden="true">
                            <div className="sp-field sp-skeleton"/>
                            <div className="sp-field sp-skeleton"/>
                        </div>
                    ) : (
                        <span className="sp-step-note">
                            Die Werte können gerade nicht geladen werden. Wir nennen sie Ihnen gern am Telefon
                            unter <a href={SUPPORT_PHONE.href}>{SUPPORT_PHONE.display}</a>.
                        </span>
                    )}
                </li>
                <li>
                    <strong>Uns Bescheid geben.</strong> Nennen Sie uns die angezeigte ID und das Passwort.
                </li>
            </Steps>

            <ContactLine/>
            <BackLink>Sie nutzen Windows?</BackLink>
        </SupportLayout>
    );
};

export default SupportMac;
